import { queryClient } from "@/common/lib/queryClient";
import { type Membership, useMe } from "@/features/accounts/api/accountsApi";
import type { AccountRole } from "@/features/accounts/api/accountsApi";
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";

// ─── localStorage helpers (exported for use in AuthCallbackPage) ────────────

const CURRENT_ACCOUNT_KEY = "mihato.currentAccountId";

export function readCurrentAccountId(): string | null {
  try {
    const value = localStorage.getItem(CURRENT_ACCOUNT_KEY);
    // Defensive: si quedó la string "undefined" o "null" de una ronda
    // previa con un setItem mal hecho, tratarlo como ausente y limpiar.
    if (value === "undefined" || value === "null" || value === "") {
      localStorage.removeItem(CURRENT_ACCOUNT_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function writeCurrentAccountId(id: string): void {
  // Defensive: TS dice string pero callers pueden pasar valores corruptos
  // (response shape inesperada, optional chaining mal). Si no es un string
  // no-vacío válido, no escribimos — mejor que persistir "undefined".
  if (typeof id !== "string" || id.length === 0 || id === "undefined" || id === "null") {
    if (typeof console !== "undefined") {
      console.warn("writeCurrentAccountId: rechazado valor inválido", id);
    }
    return;
  }
  try {
    localStorage.setItem(CURRENT_ACCOUNT_KEY, id);
  } catch {
    // swallow: incognito can block localStorage
  }
}

export function clearCurrentAccountId(): void {
  try {
    localStorage.removeItem(CURRENT_ACCOUNT_KEY);
  } catch {
    // swallow
  }
}

// ─── Context ────────────────────────────────────────────────────────────────

type CurrentAccountContextValue = {
  currentAccount: Membership["account"] | null;
  currentMembership: Membership | null;
  memberships: Membership[];
  switchAccount: (newAccountId: string) => void;
  isLoading: boolean;
};

const CurrentAccountContext = createContext<CurrentAccountContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────────────

export function AccountProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { data: me, isLoading } = useMe();
  const [currentAccountId, setCurrentAccountId] = useState<string | null>(() =>
    readCurrentAccountId(),
  );

  const activeMemberships = useMemo(
    () => me?.memberships.filter((m) => m.status === "active") ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [me?.memberships],
  );

  useEffect(() => {
    if (!me) return;

    if (activeMemberships.length === 0) {
      clearCurrentAccountId();
      setCurrentAccountId(null);
      navigate("/onboarding/create-account", { replace: true });
      return;
    }

    const storedId = readCurrentAccountId();
    const match = activeMemberships.find((m) => m.accountId === storedId);

    if (storedId && !match) {
      const first = activeMemberships[0];
      if (!first) return;
      writeCurrentAccountId(first.accountId);
      setCurrentAccountId(first.accountId);
      toast.warning(`Ya no tenés acceso a esa cuenta. Cambiaste a ${first.account.name}.`);
    } else if (!storedId) {
      const first = activeMemberships[0];
      if (!first) return;
      writeCurrentAccountId(first.accountId);
      setCurrentAccountId(first.accountId);
    }
  }, [me, activeMemberships, navigate]);

  const currentMembership = useMemo(
    () =>
      activeMemberships.find((m) => m.accountId === currentAccountId) ??
      activeMemberships[0] ??
      null,
    [activeMemberships, currentAccountId],
  );

  const currentAccount = currentMembership?.account ?? null;

  const switchAccount = useCallback(
    (newAccountId: string) => {
      const target = activeMemberships.find((m) => m.accountId === newAccountId);
      if (!target) return;
      writeCurrentAccountId(newAccountId);
      setCurrentAccountId(newAccountId);
      queryClient.clear();
      navigate("/app");
    },
    [activeMemberships, navigate],
  );

  const value = useMemo<CurrentAccountContextValue>(
    () => ({
      currentAccount,
      currentMembership,
      memberships: activeMemberships,
      switchAccount,
      isLoading,
    }),
    [currentAccount, currentMembership, activeMemberships, switchAccount, isLoading],
  );

  return <CurrentAccountContext.Provider value={value}>{children}</CurrentAccountContext.Provider>;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function useCurrentAccount(): CurrentAccountContextValue {
  const ctx = useContext(CurrentAccountContext);
  if (!ctx) throw new Error("useCurrentAccount must be used within AccountProvider");
  return ctx;
}

// ─── RequireRole guard ───────────────────────────────────────────────────────

export function RequireRole({
  roles,
  children,
}: {
  roles: AccountRole[];
  children: ReactNode;
}) {
  const { currentMembership, isLoading } = useCurrentAccount();

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-app-bg">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-500" />
      </main>
    );
  }

  if (!currentMembership || !roles.includes(currentMembership.role)) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}
